import { ReactNode } from "react";
import { Box, CircularProgress, Divider, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { fCurrencyDecimal } from "@/utils/formatNumber";
import { INVOICE_CURRENCY, INVOICE_LOCKED_CC_EMAIL, INVOICE_SECTIONS } from "@/constants";
import { EmailPreviewProps } from "@/utils/interface";
import SectionCard from "./SectionCard";

const HEADER_LABEL_WIDTH = 60;

const HeaderRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack direction="row" columnGap={2}>
    <Typography level="body-sm" textColor="neutral.500" style={{ width: HEADER_LABEL_WIDTH }}>
      {label}
    </Typography>
    {typeof children === "string" ? (
      <Typography level="body-sm" style={{ wordBreak: "break-word" }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </Stack>
);

const AmountWithCurrency = ({ total, currencyLevel = "body-xs" }: { total: number; currencyLevel?: "body-xs" }) => (
  <>
    {fCurrencyDecimal(total)}{" "}
    <Typography component="span" level={currencyLevel} textColor="neutral.500">
      {INVOICE_CURRENCY}
    </Typography>
  </>
);

const EmailPreview = ({
  invoiceTitle,
  recipientEmail,
  recipientFirstName,
  recipientLastName,
  ccEmails,
  billingMonth,
  billingYear,
  total,
  loading,
}: EmailPreviewProps) => {
  const allCcEmails = [INVOICE_LOCKED_CC_EMAIL, ...ccEmails];
  const subjectSuffix = invoiceTitle ? `(${invoiceTitle})` : "";
  const recipientName = [recipientFirstName, recipientLastName].filter(Boolean).join(" ");
  const toDisplay = recipientEmail || recipientName ? recipientName || recipientEmail : "recipient@…";

  return (
    <SectionCard title={INVOICE_SECTIONS.emailPreview} subtitle="What the recipient will see.">
      <Stack direction="column" rowGap={2}>
        <Stack direction="column" rowGap={1}>
          <HeaderRow label="From">{`Financeable Billing <${INVOICE_LOCKED_CC_EMAIL}>`}</HeaderRow>
          <HeaderRow label="To">{toDisplay}</HeaderRow>
          {/* Will enable code when we support custom CC emails */}
          {/* <HeaderRow label="CC">{allCcEmails.join(", ")}</HeaderRow>
          <HeaderRow label="Subject">{`Invoice from Financeable — ${subjectSuffix}`}</HeaderRow> */}
        </Stack>

        <Divider />

        <Typography level="body-sm">Hi there,</Typography>
        <Typography level="body-sm">
          Please find your invoice for{" "}
          <Typography component="span" style={{ fontWeight: 700 }}>
            {billingMonth} {billingYear}
          </Typography>
          . Amount due{" "}
          <Typography component="span" style={{ fontWeight: 700 }}>
            {fCurrencyDecimal(total)} {INVOICE_CURRENCY}
          </Typography>
          .
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: "neutral.300",
            borderRadius: "8px",
            padding: 3,
            alignItems: "center",
            rowGap: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
            <Typography level="body-md" textColor="neutral.500" style={{ letterSpacing: 1 }}>
              AMOUNT DUE:
            </Typography>
            {loading ? (
              <CircularProgress size="sm" />
            ) : (
              <Typography level="title-lg" style={{ fontWeight: 700 }}>
                <AmountWithCurrency total={total} />
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </SectionCard>
  );
};

export default EmailPreview;
